import { inject, Injectable, untracked } from '@angular/core';
import { ConsentGroupName, ConsentServiceName } from 'libs/shared/src/models';
import { EnvironmentService } from '../../../environment/environment.service';
import { BaseScript } from '../base.script';
import { noop } from '../../../../utils/noop.util';

declare global {
  interface Window {
    googleMapsLoader?: ((...args: any[]) => void) & { q?: any[] };
    initGoogleMaps?: () => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsScript extends BaseScript {
  // Properties
  /** The name of the script */
  public name: string = 'Google Maps';
  /** The consent category of the script */
  public consentCategory: ConsentGroupName = 'functionality';
  /** The consent service of the script */
  public consentService: ConsentServiceName = 'gmaps';
  /**  Callback function that is called before the script is created. */
  public override beforeCreate = (): void => {
    // Define the callback that will be executed when the Google Maps API is loaded (afterInitMaps)
    window.initGoogleMaps = () => {
      // Google calls back from a microtask that zone.js may drain while a computed/template is evaluating (NG0600)
      untracked(() => this.isLoaded.set(true));
      console.debug('Google Maps API loaded successfully with async pattern');
    };
  };
  /**  Callback function that is called after the script is created. (override because the loading is handled by the callback) */
  public override afterCreate?: (() => Promise<void> | void) | undefined = noop;

  // Getters & Setters
  /** The API key of Google Maps inside the environment */
  private get apiKey(): string | undefined {
    return this.envService.env.GOOGLE_MAPS_KEY;
  }

  // Services
  private readonly envService: EnvironmentService = inject(EnvironmentService);

  // Methods
  constructor() {
    super();
    if (!this.apiKey || this.apiKey === 'shouldBeOverwritten' || this.apiKey.trim() === '') {
      console.error('Google Maps API key is missing or invalid:', this.apiKey);
    }
  }

  /**
   * Gets the attributes for the script element.
   * @returns A record of attributes for the script element.
   */
  public getScriptAttributes(): Record<string, string> {
    return {
      async: 'true',
      defer: 'true'
    };
  }

  /**
   * Gets the script ID.
   * @returns The script ID.
   */
  public getScriptId(): string {
    return 'google-maps-script';
  }

  /**
   * Gets the script URL.
   * @returns The script URL.
   */
  public getScriptUrl(): string {
    return `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&callback=initGoogleMaps&loading=async`;
  }
}
